<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>操作日志</span>
        <div class="filters">
          <el-select v-model="filterModule" placeholder="模块" clearable style="width: 140px" @change="handleSearch">
            <el-option v-for="opt in moduleOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
          <el-select v-model="filterOperationType" placeholder="操作类型" clearable style="width: 120px" @change="handleSearch">
            <el-option v-for="opt in operationTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
          <el-input
            v-model="filterKeyword"
            placeholder="操作人/内容/目标ID"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-date-picker
            v-model="filterDateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 360px"
            @change="handleSearch"
          />
          <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
        </div>
      </div>
      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column prop="createdAt" label="操作时间" min-width="160" :formatter="formatTime" />
        <el-table-column prop="adminName" label="操作人" width="110" show-overflow-tooltip />
        <el-table-column prop="ip" label="IP" width="130" show-overflow-tooltip />
        <el-table-column prop="moduleName" label="模块" width="110" />
        <el-table-column prop="operationTypeLabel" label="类型" width="70" />
        <el-table-column prop="actionName" label="操作" min-width="120" show-overflow-tooltip />
        <el-table-column label="目标" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span v-if="row.targetId">{{ row.targetType ? row.targetType + ':' : '' }}{{ row.targetId }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="变更内容" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ contentSummary(row.content) }}
          </template>
        </el-table-column>
        <el-table-column prop="resultStatusLabel" label="结果" width="70">
          <template slot-scope="{ row }">
            <el-tag :type="row.resultStatus === 1 ? 'success' : 'danger'" size="mini">
              {{ row.resultStatusLabel }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="handlePageChange"
      />
    </el-card>

    <el-dialog title="操作详情" :visible.sync="detailVisible" width="640px" top="8vh">
      <div v-if="detailData" class="detail-box">
        <div class="detail-row">
          <span class="detail-label">操作时间</span>
          <span>{{ formatTime(null, null, detailData.createdAt) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">操作人</span>
          <span>{{ detailData.adminName || '-' }}（ID: {{ detailData.adminId }}）</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">IP</span>
          <span>{{ detailData.ip || '-' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">模块</span>
          <span>{{ detailData.moduleName }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">操作</span>
          <span>{{ detailData.operationTypeLabel }} / {{ detailData.actionName }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">请求</span>
          <span>{{ detailData.requestMethod }} {{ detailData.requestPath }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">结果</span>
          <el-tag :type="detailData.resultStatus === 1 ? 'success' : 'danger'" size="mini">
            {{ detailData.resultStatusLabel }}
          </el-tag>
          <span v-if="detailData.errorMessage" class="error-msg">{{ detailData.errorMessage }}</span>
        </div>
        <div class="detail-row detail-row-block">
          <span class="detail-label">变更内容</span>
          <pre class="detail-content">{{ formatContent(detailData.content) }}</pre>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getOperationLogs } from '@/api/admin'
import listPage from '@/mixins/listPage'

function formatDateTimeStr(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const MODULE_OPTIONS = [
  { value: 'users', label: '用户管理' },
  { value: 'works', label: '作品管理' },
  { value: 'trade', label: '交易管理' },
  { value: 'demands', label: '需求管理' },
  { value: 'faqs', label: 'FAQ管理' },
  { value: 'cms', label: '内容管理' },
  { value: 'feedback', label: '反馈管理' },
  { value: 'comments', label: '评论管理' },
  { value: 'compliance', label: '合规管理' },
  { value: 'agents', label: '代理商管理' },
  { value: 'system', label: '系统管理' }
]

const OPERATION_TYPE_OPTIONS = [
  { value: 'create', label: '新增' },
  { value: 'update', label: '修改' },
  { value: 'delete', label: '删除' }
]

export default {
  name: 'OperationLogs',
  mixins: [listPage],
  data() {
    return {
      moduleOptions: MODULE_OPTIONS,
      operationTypeOptions: OPERATION_TYPE_OPTIONS,
      filterModule: '',
      filterOperationType: '',
      filterKeyword: '',
      filterDateRange: null,
      detailVisible: false,
      detailData: null
    }
  },
  created() {
    this.pageSize = 20
    this.loadList()
  },
  methods: {
    async loadList() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          size: this.pageSize,
          moduleCode: this.filterModule || undefined,
          operationType: this.filterOperationType || undefined,
          keyword: this.filterKeyword || undefined
        }
        if (this.filterDateRange && this.filterDateRange.length === 2) {
          params.startTime = this.filterDateRange[0]
          params.endTime = this.filterDateRange[1]
        }
        const result = await getOperationLogs(params)
        this.list = (result && result.list) || []
        this.total = (result && result.total) || 0
      } catch (e) {
        this.$message.error(e.message || '加载操作日志失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.loadList()
    },
    formatTime(row, column, value) {
      return formatDateTimeStr(value)
    },
    contentSummary(content) {
      if (!content) return '-'
      try {
        const obj = JSON.parse(content)
        return obj.summary || '-'
      } catch (e) {
        return content.length > 80 ? content.slice(0, 80) + '...' : content
      }
    },
    formatContent(content) {
      if (!content) return '-'
      try {
        return JSON.stringify(JSON.parse(content), null, 2)
      } catch (e) {
        return content
      }
    },
    openDetail(row) {
      this.detailData = row
      this.detailVisible = true
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
.detail-box {
  font-size: 14px;
  color: #334155;
}
.detail-row {
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  align-items: flex-start;
}
.detail-row-block {
  flex-direction: column;
}
.detail-label {
  width: 72px;
  flex-shrink: 0;
  color: #64748b;
}
.detail-content {
  margin: 6px 0 0;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 320px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.5;
}
.error-msg {
  margin-left: 8px;
  color: #dc2626;
}
</style>
